import { ChevronDownIcon, ChevronRightIcon } from '@orn/shared-web/icons';
import { cx } from '@orn/shared-web/utils/cx';
import formatTimeAgo from '@orn/shared-web/utils/datetime/formatTimeAgo';
import * as React from 'react';

import Button from '../../components/Button';
import IconButton from '../../components/IconButton';
import Table from '../../components/Table';

import AccountDetailPanel from './AccountDetailPanel';
import {
  EMPTY_CELL,
  accountRowFlags,
  availableOrLimitLabel,
  formatMajorUnits,
  isGapNotable,
} from './accountDetailView';
import FlagBadges, { BADGE_CLASS } from './FlagBadges';

import type {
  AdminGetFamilyBankConnectionsQuery,
  AdminGetFinancialAccountDetailsQuery,
} from './__generated__/BankConnectionsScene.gql';

type ConnectionAccount =
  AdminGetFamilyBankConnectionsQuery['adminGetFamilyBankConnections'][number]['accounts'][number];
export type AccountDetailRow = AdminGetFinancialAccountDetailsQuery['adminGetFinancialAccountDetails'][number];

const COLUMN_COUNT = 9;

const accountTypeLabel = (type: string, subtype: string | null | undefined): string =>
  (subtype ? `${type} · ${subtype}` : type).replaceAll('_', ' ').toLowerCase();
const CELL_CLASS = 'px-2 py-1 text-xs';
const NUMERIC_CELL_CLASS = `${CELL_CLASS} text-right`;
const HEADER_CELL_CLASS = 'px-2 py-1';

interface IAccountsTableProps {
  familyId: string;
  /** Every account on the connection, suppressed ones included. */
  accounts: readonly ConnectionAccount[];
  /** Detail rows by account id. An account with none still renders, as dashes, and cannot expand. */
  detailsById: ReadonlyMap<string, AccountDetailRow>;
  /** The account a suppress or restore is in flight for. */
  actionLoadingId: string | null;
  onSuppress: (account: { id: string; name: string }) => void;
  onRestore: (financialAccountId: string) => void;
}

/**
 * One connection's accounts as a dense table: newest balance, holdings value
 * against it, and data-quality flags per row, with a lazily loaded detail
 * panel under any visible account. A suppressed account keeps its row and its
 * Restore action but shows no data and does not expand.
 */
const AccountsTable = ({
  familyId,
  accounts,
  detailsById,
  actionLoadingId,
  onSuppress,
  onRestore,
}: IAccountsTableProps) => {
  const [expanded, setExpanded] = React.useState<Set<string>>(new Set());

  // A suppressed account loses its detail; forget it was open so a later restore starts collapsed.
  React.useEffect(() => {
    const suppressedIds = accounts.filter((account) => account.ignoredAt).map((account) => account.id);
    setExpanded((prev) => {
      if (!suppressedIds.some((id) => prev.has(id))) {
        return prev;
      }
      const next = new Set(prev);
      suppressedIds.forEach((id) => next.delete(id));
      return next;
    });
  }, [accounts]);

  const toggleExpanded = (financialAccountId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(financialAccountId)) {
        next.delete(financialAccountId);
      } else {
        next.add(financialAccountId);
      }
      return next;
    });
  };

  return (
    <Table className="mt-1.5 table-fixed">
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell className={`${HEADER_CELL_CLASS} w-8`} />
          <Table.HeaderCell className={HEADER_CELL_CLASS}>Account</Table.HeaderCell>
          <Table.HeaderCell className={`${HEADER_CELL_CLASS} w-32`}>Type</Table.HeaderCell>
          <Table.HeaderCell className={`${HEADER_CELL_CLASS} w-28 text-right`}>Balance</Table.HeaderCell>
          <Table.HeaderCell className={`${HEADER_CELL_CLASS} w-36 text-right`}>Avail / limit</Table.HeaderCell>
          <Table.HeaderCell className={`${HEADER_CELL_CLASS} w-24`}>As of</Table.HeaderCell>
          <Table.HeaderCell className={`${HEADER_CELL_CLASS} w-36 text-right`}>Holdings</Table.HeaderCell>
          <Table.HeaderCell className={`${HEADER_CELL_CLASS} w-24 text-right`}>Gap</Table.HeaderCell>
          <Table.HeaderCell className={`${HEADER_CELL_CLASS} w-72`}>Flags</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {accounts.map((account) => {
          const isSuppressed = Boolean(account.ignoredAt);
          const detail = isSuppressed ? undefined : detailsById.get(account.id);
          const audit = detail?.investmentAudit ?? null;
          const isExpanded = detail !== undefined && expanded.has(account.id);
          const isActing = actionLoadingId === account.id;
          const currency = { isoCurrencyCode: detail?.currencyCode };

          return (
            <React.Fragment key={account.id}>
              <Table.Row className={cx(isExpanded && 'bg-bg-secondary')}>
                <Table.Cell className={CELL_CLASS}>
                  {detail && (
                    <IconButton
                      label={isExpanded ? 'Hide account detail' : 'Show account detail'}
                      aria-expanded={isExpanded}
                      onClick={() => toggleExpanded(account.id)}
                    >
                      {isExpanded ? (
                        <ChevronDownIcon className="size-3.5" />
                      ) : (
                        <ChevronRightIcon className="size-3.5" />
                      )}
                    </IconButton>
                  )}
                </Table.Cell>
                <Table.Cell muted={isSuppressed} className={`${CELL_CLASS} truncate font-medium`} title={account.name}>
                  {account.name}
                  {account.mask && <span className="text-fg-tertiary font-normal"> ···{account.mask}</span>}
                </Table.Cell>
                <Table.Cell
                  mono
                  muted
                  className={`${CELL_CLASS} truncate text-[11px] lowercase`}
                  title={accountTypeLabel(account.type, account.subtype)}
                >
                  {(account.subtype ?? account.type).replaceAll('_', ' ')}
                </Table.Cell>
                <Table.Cell className={NUMERIC_CELL_CLASS}>
                  {formatMajorUnits(detail?.currentBalance, currency)}
                </Table.Cell>
                <Table.Cell className={NUMERIC_CELL_CLASS}>
                  {detail
                    ? availableOrLimitLabel({
                        type: account.type,
                        availableBalance: detail.availableBalance,
                        creditLimit: detail.creditLimit,
                        currencyCode: detail.currencyCode,
                      })
                    : EMPTY_CELL}
                </Table.Cell>
                <Table.Cell
                  muted
                  className={CELL_CLASS}
                  title={detail?.balanceAsOf ? new Date(detail.balanceAsOf).toLocaleString() : undefined}
                >
                  {detail?.balanceAsOf ? formatTimeAgo(detail.balanceAsOf) : EMPTY_CELL}
                </Table.Cell>
                <Table.Cell className={NUMERIC_CELL_CLASS}>
                  {audit?.holdingsValue != null ? (
                    <>
                      {formatMajorUnits(audit.holdingsValue, currency)}
                      <span className="text-fg-tertiary"> ({audit.positionCount})</span>
                    </>
                  ) : (
                    EMPTY_CELL
                  )}
                </Table.Cell>
                <Table.Cell
                  className={cx(
                    NUMERIC_CELL_CLASS,
                    audit && isGapNotable(audit.balanceGap, audit.positions) && 'text-fg-warning-primary'
                  )}
                >
                  {formatMajorUnits(audit?.balanceGap, currency)}
                </Table.Cell>
                <Table.Cell className={CELL_CLASS}>
                  <div className="flex items-center gap-2">
                    {isSuppressed ? (
                      <span className={cx(BADGE_CLASS, 'bg-warning-primary text-fg-warning-primary')}>Suppressed</span>
                    ) : (
                      <FlagBadges flags={accountRowFlags(audit)} />
                    )}
                    <Button
                      onClick={() =>
                        isSuppressed ? onRestore(account.id) : onSuppress({ id: account.id, name: account.name })
                      }
                      isDisabled={isActing}
                      isLoading={isActing}
                      color={isSuppressed ? 'secondary' : 'danger'}
                      size="xs"
                      className="ml-auto shrink-0"
                    >
                      {isSuppressed ? 'Restore' : 'Suppress'}
                    </Button>
                  </div>
                </Table.Cell>
              </Table.Row>
              {isExpanded && (
                <Table.Row className="hover:bg-transparent">
                  <Table.Cell className="bg-bg-secondary px-3 py-2.5" colSpan={COLUMN_COUNT}>
                    <AccountDetailPanel familyId={familyId} financialAccountId={account.id} />
                  </Table.Cell>
                </Table.Row>
              )}
            </React.Fragment>
          );
        })}
      </Table.Body>
    </Table>
  );
};

export default AccountsTable;
